import { ADUModel, PoolModel, DesignObject } from '../../store/designStore'

// Smart placement recommendation types
export interface PlacementRecommendation {
  id: string
  position: [number, number, number]
  rotation: [number, number, number]
  confidence: number // 0-1
  reasoning: string
  category: 'optimal' | 'good' | 'acceptable' | 'warning'
  considerations: PlacementConsideration[]
}

export interface PlacementConsideration {
  type: 'setback' | 'privacy' | 'access' | 'utilities' | 'sun_exposure' | 'drainage' | 'aesthetics' | 'safety'
  impact: 'positive' | 'negative' | 'neutral'
  description: string
  weight: number // 0-1
}

export interface PropertyConstraints {
  boundaries: {
    width: number
    depth: number
    setbacks: {
      front: number
      rear: number
      side: number
    }
  }
  existingStructures: {
    house: { position: [number, number, number]; dimensions: [number, number, number] }
    driveway?: { position: [number, number, number]; dimensions: [number, number, number] }
    utilities?: Array<{ position: [number, number, number]; type: string; clearance: number }>
  }
  terrain: {
    slope: number // degrees
    drainage: 'excellent' | 'good' | 'fair' | 'poor'
    soilType: 'clay' | 'sand' | 'loam' | 'rock'
  }
  zoning: {
    maxCoverage: number // percentage
    maxHeight: number // feet
    minSetbacks: {
      front: number
      rear: number
      side: number
    }
  }
}

export class SmartPlacementAI {
  private propertyConstraints: PropertyConstraints
  private existingObjects: DesignObject[]

  constructor(propertyConstraints: PropertyConstraints, existingObjects: DesignObject[] = []) {
    this.propertyConstraints = propertyConstraints
    this.existingObjects = existingObjects
  }

  // Main placement recommendation engine
  generatePlacementRecommendations(
    objectType: 'ADU' | 'Pool',
    objectDimensions: { width: number; height: number; depth: number },
    preferences: {
      prioritizePrivacy?: boolean
      prioritizeAccess?: boolean
      prioritizeSunExposure?: boolean
      avoidUtilities?: boolean
    } = {}
  ): PlacementRecommendation[] {
    const recommendations: PlacementRecommendation[] = []
    const gridSize = 2 // 2-foot grid for placement testing

    // Generate potential positions across the property
    for (let x = -this.propertyConstraints.boundaries.width / 2; x <= this.propertyConstraints.boundaries.width / 2; x += gridSize) {
      for (let z = -this.propertyConstraints.boundaries.depth / 2; z <= this.propertyConstraints.boundaries.depth / 2; z += gridSize) {
        const position: [number, number, number] = [x, 0, z]
        
        // Check if position is valid
        if (this.isPositionValid(position, objectDimensions)) {
          const recommendation = this.evaluatePosition(position, objectType, objectDimensions, preferences)
          if (recommendation.confidence > 0.3) { // Only include viable options
            recommendations.push(recommendation)
          }
        }
      }
    }

    // Sort by confidence and return top recommendations
    return recommendations
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, 10) // Top 10 recommendations
  }

  // Evaluate a specific position
  private evaluatePosition(
    position: [number, number, number],
    objectType: 'ADU' | 'Pool',
    objectDimensions: { width: number; height: number; depth: number },
    preferences: any
  ): PlacementRecommendation {
    const considerations: PlacementConsideration[] = []
    let totalScore = 0
    let totalWeight = 0

    // 1. Setback compliance
    const setbackScore = this.evaluateSetbacks(position, objectDimensions)
    considerations.push({
      type: 'setback',
      impact: setbackScore.compliant ? 'positive' : 'negative',
      description: setbackScore.description,
      weight: 0.25
    })
    totalScore += setbackScore.score * 0.25
    totalWeight += 0.25

    // 2. Privacy considerations
    const privacyScore = this.evaluatePrivacy(position, objectType, preferences.prioritizePrivacy)
    considerations.push({
      type: 'privacy',
      impact: privacyScore > 0.7 ? 'positive' : privacyScore > 0.4 ? 'neutral' : 'negative',
      description: this.getPrivacyDescription(privacyScore),
      weight: preferences.prioritizePrivacy ? 0.2 : 0.1
    })
    const privacyWeight = preferences.prioritizePrivacy ? 0.2 : 0.1
    totalScore += privacyScore * privacyWeight
    totalWeight += privacyWeight

    // 3. Access evaluation
    const accessScore = this.evaluateAccess(position, objectType, preferences.prioritizeAccess)
    considerations.push({
      type: 'access',
      impact: accessScore > 0.7 ? 'positive' : accessScore > 0.4 ? 'neutral' : 'negative',
      description: this.getAccessDescription(accessScore, objectType),
      weight: preferences.prioritizeAccess ? 0.2 : 0.15
    })
    const accessWeight = preferences.prioritizeAccess ? 0.2 : 0.15
    totalScore += accessScore * accessWeight
    totalWeight += accessWeight

    // 4. Sun exposure (important for pools and ADU comfort)
    const sunScore = this.evaluateSunExposure(position, objectType, preferences.prioritizeSunExposure)
    considerations.push({
      type: 'sun_exposure',
      impact: sunScore > 0.6 ? 'positive' : sunScore > 0.3 ? 'neutral' : 'negative',
      description: this.getSunExposureDescription(sunScore, objectType),
      weight: preferences.prioritizeSunExposure ? 0.2 : 0.1
    })
    const sunWeight = preferences.prioritizeSunExposure ? 0.2 : 0.1
    totalScore += sunScore * sunWeight
    totalWeight += sunWeight

    // 5. Utilities clearance
    const utilityScore = this.evaluateUtilities(position, objectDimensions, preferences.avoidUtilities)
    considerations.push({
      type: 'utilities',
      impact: utilityScore > 0.8 ? 'positive' : utilityScore > 0.5 ? 'neutral' : 'negative',
      description: this.getUtilityDescription(utilityScore),
      weight: 0.15
    })
    totalScore += utilityScore * 0.15
    totalWeight += 0.15

    // 6. Drainage considerations
    const drainageScore = this.evaluateDrainage(position, objectType)
    considerations.push({
      type: 'drainage',
      impact: drainageScore > 0.7 ? 'positive' : drainageScore > 0.4 ? 'neutral' : 'negative',
      description: this.getDrainageDescription(drainageScore, objectType),
      weight: objectType === 'Pool' ? 0.2 : 0.1
    })
    const drainageWeight = objectType === 'Pool' ? 0.2 : 0.1
    totalScore += drainageScore * drainageWeight
    totalWeight += drainageWeight

    // Calculate final confidence score
    const confidence = totalWeight > 0 ? totalScore / totalWeight : 0
    
    // Determine category based on confidence
    let category: 'optimal' | 'good' | 'acceptable' | 'warning'
    if (confidence >= 0.8) category = 'optimal'
    else if (confidence >= 0.6) category = 'good'
    else if (confidence >= 0.4) category = 'acceptable'
    else category = 'warning'

    // Generate reasoning
    const reasoning = this.generateReasoning(considerations, category, objectType)

    return {
      id: `placement-${position[0]}-${position[2]}`,
      position,
      rotation: [0, 0, 0], // Default rotation, could be optimized
      confidence,
      reasoning,
      category,
      considerations
    }
  }

  // Check if position is within property boundaries and doesn't conflict
  private isPositionValid(
    position: [number, number, number],
    objectDimensions: { width: number; height: number; depth: number }
  ): boolean {
    const [x, , z] = position
    const { width, depth } = objectDimensions
    const bounds = this.propertyConstraints.boundaries

    // Check property boundaries
    if (x - width/2 < -bounds.width/2 || x + width/2 > bounds.width/2) return false
    if (z - depth/2 < -bounds.depth/2 || z + depth/2 > bounds.depth/2) return false

    // Check collision with existing structures
    const house = this.propertyConstraints.existingStructures.house
    if (this.checkCollision(position, objectDimensions, house.position, house.dimensions)) {
      return false
    }

    // Check collision with existing objects
    for (const obj of this.existingObjects) {
      // Convert dimensions to a consistent format for collision checking
      const normalizedDimensions = 'height' in objectDimensions 
        ? objectDimensions 
        : { width: (objectDimensions as any).width, height: 3, depth: (objectDimensions as any).length || (objectDimensions as any).depth }
      
      // Also normalize obj.dimensions for collision checking
      const normalizedObjDimensions = 'height' in obj.dimensions
        ? obj.dimensions
        : { width: (obj.dimensions as any).width, height: 3, depth: (obj.dimensions as any).length || (obj.dimensions as any).depth }
      
      if (this.checkCollision(position, normalizedDimensions, obj.position, normalizedObjDimensions)) {
        return false
      }
    }

    return true
  }

  // Check collision between two rectangular objects
  private checkCollision(
    pos1: [number, number, number],
    dim1: { width: number; height: number; depth: number },
    pos2: [number, number, number],
    dim2: [number, number, number] | { width: number; height: number; depth: number }
  ): boolean {
    const [x1, , z1] = pos1
    const [x2, , z2] = pos2
    
    const w1 = dim1.width
    const d1 = dim1.depth
    const w2 = Array.isArray(dim2) ? dim2[0] : dim2.width
    const d2 = Array.isArray(dim2) ? dim2[2] : dim2.depth

    // Add 2-foot buffer
    const buffer = 2

    return !(
      x1 - w1/2 > x2 + w2/2 + buffer ||
      x1 + w1/2 < x2 - w2/2 - buffer ||
      z1 - d1/2 > z2 + d2/2 + buffer ||
      z1 + d1/2 < z2 - d2/2 - buffer
    )
  }

  // Evaluation methods for different criteria
  private evaluateSetbacks(
    position: [number, number, number],
    objectDimensions: { width: number; height: number; depth: number }
  ): { score: number; compliant: boolean; description: string } {
    const [x, , z] = position
    const { width, depth } = objectDimensions
    const bounds = this.propertyConstraints.boundaries
    const setbacks = this.propertyConstraints.zoning.minSetbacks

    const frontSetback = (bounds.depth/2 + z - depth/2)
    const rearSetback = (bounds.depth/2 - z - depth/2)
    const leftSetback = (bounds.width/2 + x - width/2)
    const rightSetback = (bounds.width/2 - x - width/2)

    const compliant = 
      frontSetback >= setbacks.front &&
      rearSetback >= setbacks.rear &&
      leftSetback >= setbacks.side &&
      rightSetback >= setbacks.side

    const minActualSetback = Math.min(frontSetback, rearSetback, leftSetback, rightSetback)
    const minRequiredSetback = Math.min(setbacks.front, setbacks.rear, setbacks.side)
    
    const score = compliant ? Math.min(1, minActualSetback / (minRequiredSetback * 2)) : 0

    const description = compliant 
      ? `Meets all setback requirements (${minActualSetback.toFixed(1)}' minimum)`
      : `Violates setback requirements (${minActualSetback.toFixed(1)}' vs ${minRequiredSetback}' required)`

    return { score, compliant, description }
  }

  private evaluatePrivacy(position: [number, number, number], objectType: 'ADU' | 'Pool', prioritize?: boolean): number {
    const [x, , z] = position
    const bounds = this.propertyConstraints.boundaries
    
    // Distance from property edges (privacy from neighbors)
    const edgeDistances = [
      bounds.width/2 + x,  // left edge
      bounds.width/2 - x,  // right edge
      bounds.depth/2 + z,  // front edge
      bounds.depth/2 - z   // rear edge
    ]
    
    const minEdgeDistance = Math.min(...edgeDistances)
    const maxEdgeDistance = Math.max(...edgeDistances)
    
    // Privacy score based on distance from edges
    let privacyScore = Math.min(1, minEdgeDistance / 15) // 15 feet for good privacy
    
    // Bonus for being in rear of property (more private)
    if (z < -bounds.depth/4) {
      privacyScore += 0.2
    }
    
    // ADUs benefit more from privacy than pools
    if (objectType === 'ADU') {
      privacyScore *= 1.1
    }
    
    return Math.min(1, privacyScore)
  }

  private evaluateAccess(position: [number, number, number], objectType: 'ADU' | 'Pool', prioritize?: boolean): number {
    const [x, , z] = position
    const house = this.propertyConstraints.existingStructures.house
    const driveway = this.propertyConstraints.existingStructures.driveway
    
    // Distance to house (closer is better for access)
    const houseDistance = Math.sqrt(
      Math.pow(x - house.position[0], 2) + 
      Math.pow(z - house.position[2], 2)
    )
    
    let accessScore = Math.max(0, 1 - houseDistance / 50) // 50 feet max reasonable distance
    
    // Bonus for proximity to driveway (construction access)
    if (driveway) {
      const driveDistance = Math.sqrt(
        Math.pow(x - driveway.position[0], 2) + 
        Math.pow(z - driveway.position[2], 2)
      )
      accessScore += Math.max(0, 0.3 * (1 - driveDistance / 30))
    }
    
    // ADUs need better access than pools
    if (objectType === 'ADU') {
      accessScore *= 1.2
    }
    
    return Math.min(1, accessScore)
  }

  private evaluateSunExposure(position: [number, number, number], objectType: 'ADU' | 'Pool', prioritize?: boolean): number {
    const [x, , z] = position
    const house = this.propertyConstraints.existingStructures.house
    
    // Check if position is in shadow of house (simplified)
    const houseBlocks = (
      x >= house.position[0] - house.dimensions[0]/2 - 5 &&
      x <= house.position[0] + house.dimensions[0]/2 + 5 &&
      z <= house.position[2] - 5 // South of house gets shadowed
    )
    
    let sunScore = houseBlocks ? 0.3 : 0.8
    
    // South-facing positions get bonus sun
    if (z < 0) { // Assuming north is positive Z
      sunScore += 0.2
    }
    
    // Pools benefit more from sun exposure
    if (objectType === 'Pool') {
      sunScore *= 1.2
    }
    
    return Math.min(1, sunScore)
  }

  private evaluateUtilities(
    position: [number, number, number],
    objectDimensions: { width: number; height: number; depth: number },
    avoidUtilities?: boolean
  ): number {
    const utilities = this.propertyConstraints.existingStructures.utilities || []
    let minDistance = Infinity
    
    for (const utility of utilities) {
      const distance = Math.sqrt(
        Math.pow(position[0] - utility.position[0], 2) + 
        Math.pow(position[2] - utility.position[2], 2)
      )
      minDistance = Math.min(minDistance, distance - utility.clearance)
    }
    
    if (minDistance === Infinity) return 1 // No utilities to worry about
    
    // Score based on clearance distance
    return Math.max(0, Math.min(1, minDistance / 10)) // 10 feet for good clearance
  }

  private evaluateDrainage(position: [number, number, number], objectType: 'ADU' | 'Pool'): number {
    const terrain = this.propertyConstraints.terrain
    
    let drainageScore = 0.5 // Base score
    
    // Adjust based on terrain drainage
    switch (terrain.drainage) {
      case 'excellent': drainageScore = 0.9; break
      case 'good': drainageScore = 0.7; break
      case 'fair': drainageScore = 0.5; break
      case 'poor': drainageScore = 0.2; break
    }
    
    // Pools are more sensitive to drainage
    if (objectType === 'Pool' && terrain.drainage === 'poor') {
      drainageScore *= 0.5
    }
    
    // Slope considerations
    if (terrain.slope > 5) { // Steep slope
      drainageScore *= objectType === 'Pool' ? 0.6 : 0.8
    }
    
    return drainageScore
  }

  // Description generators
  private getPrivacyDescription(score: number): string {
    if (score > 0.7) return 'Excellent privacy from neighbors'
    if (score > 0.4) return 'Good privacy with some visibility'
    return 'Limited privacy, visible from neighboring properties'
  }

  private getAccessDescription(score: number, objectType: string): string {
    if (score > 0.7) return `Excellent access for ${objectType.toLowerCase()} construction and maintenance`
    if (score > 0.4) return `Good access with reasonable construction routes`
    return `Limited access, may require special construction considerations`
  }

  private getSunExposureDescription(score: number, objectType: string): string {
    if (objectType === 'Pool') {
      if (score > 0.6) return 'Excellent sun exposure for pool heating and enjoyment'
      if (score > 0.3) return 'Moderate sun exposure, some shading during day'
      return 'Limited sun exposure, may require additional heating'
    } else {
      if (score > 0.6) return 'Good natural lighting for ADU'
      if (score > 0.3) return 'Moderate natural lighting'
      return 'Limited natural lighting, may feel darker inside'
    }
  }

  private getUtilityDescription(score: number): string {
    if (score > 0.8) return 'Safe distance from all utilities'
    if (score > 0.5) return 'Adequate clearance from utilities'
    return 'Close to utilities, may require special precautions'
  }

  private getDrainageDescription(score: number, objectType: string): string {
    if (objectType === 'Pool') {
      if (score > 0.7) return 'Excellent drainage for pool installation'
      if (score > 0.4) return 'Good drainage with proper preparation'
      return 'Poor drainage, may require extensive site preparation'
    } else {
      if (score > 0.7) return 'Good drainage around ADU foundation'
      if (score > 0.4) return 'Adequate drainage with proper grading'
      return 'Drainage concerns, may require additional foundation work'
    }
  }

  private generateReasoning(
    considerations: PlacementConsideration[],
    category: string,
    objectType: string
  ): string {
    const positive = considerations.filter(c => c.impact === 'positive')
    const negative = considerations.filter(c => c.impact === 'negative')
    
    let reasoning = `This ${objectType.toLowerCase()} placement is rated as ${category}. `
    
    if (positive.length > 0) {
      reasoning += `Strengths: ${positive.map(c => c.type.replace('_', ' ')).join(', ')}. `
    }
    
    if (negative.length > 0) {
      reasoning += `Considerations: ${negative.map(c => c.type.replace('_', ' ')).join(', ')}. `
    }
    
    return reasoning
  }
}

// Default property constraints for demo purposes
export const DEFAULT_PROPERTY_CONSTRAINTS: PropertyConstraints = {
  boundaries: {
    width: 80,
    depth: 120,
    setbacks: {
      front: 25,
      rear: 10,
      side: 5
    }
  },
  existingStructures: {
    house: {
      position: [0, 0, 30],
      dimensions: [40, 12, 30]
    },
    driveway: {
      position: [-20, 0, 45],
      dimensions: [12, 0.5, 20]
    },
    utilities: [
      { position: [15, 0, 35], type: 'electrical', clearance: 5 },
      { position: [-10, 0, 20], type: 'gas', clearance: 3 }
    ]
  },
  terrain: {
    slope: 2,
    drainage: 'good',
    soilType: 'loam'
  },
  zoning: {
    maxCoverage: 40,
    maxHeight: 16,
    minSetbacks: {
      front: 25,
      rear: 10,
      side: 5
    }
  }
}

export default SmartPlacementAI
